import { useEffect, useState } from 'react';
import axios from 'axios';
import { Building2, Home, Images, FileText, Users, Sparkles, AlertTriangle, CheckCircle2, ArrowRight, Loader2 } from 'lucide-react';
import ProjectTimeline from './ProjectTimeline';
import ProjectTreeViewer from './ProjectTreeViewer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const opts = { withCredentials: true };
const BASE = process.env.REACT_APP_BACKEND_URL;

const Kpi = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="rounded-xl border border-[var(--c-border)] bg-[var(--c-surface-0)] p-4 flex items-start gap-3" data-testid={testid}>
    <div className="w-8 h-8 rounded-lg bg-[var(--c-gold-soft)] text-[var(--c-gold)] flex items-center justify-center shrink-0"><Icon className="w-4 h-4" strokeWidth={1.75} /></div>
    <div className="min-w-0">
      <p className="text-[10px] uppercase tracking-[0.16em] text-[var(--c-text-faint)]">{label}</p>
      <p className="text-[22px] leading-tight font-semibold hud-tabular text-[var(--c-text)]" style={{ fontFamily: 'Outfit, sans-serif' }}>{value}</p>
      {sub && <p className="text-[11px] text-[var(--c-text-muted)] truncate">{sub}</p>}
    </div>
  </div>
);

const Card = ({ title, children, action, testid }) => (
  <div className="rounded-xl border border-[var(--c-border)] bg-[var(--c-surface-0)] p-4" data-testid={testid}>
    <div className="flex items-center justify-between mb-3"><p className="text-[10px] uppercase tracking-[0.18em] text-[var(--c-text-faint)]">{title}</p>{action}</div>
    {children}
  </div>
);

const fmtMoney = (n) => (n ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n) : '—');

/** ProjectDashboardSection — V17.0 : KPI, arborescence + viewer, avancement, jalons, alertes, dernier rendu validé. */
const ProjectDashboardSection = ({ project, projectId, onOpen, onNavigateToCalendar }) => {
  const [tree, setTree] = useState(null);
  const [ov, setOv] = useState(null);
  const [crm, setCrm] = useState({ contacts: 0, opps: 0, amount: 0 });
  useEffect(() => {
    if (!projectId) return;
    axios.get(`${API}/projects/${projectId}/maquette/tree`, opts).then((r) => setTree(r.data)).catch(() => setTree(null));
    axios.get(`${API}/projects/${projectId}/overview`, opts).then((r) => setOv(r.data)).catch(() => setOv(null));
    Promise.all([
      axios.get(`${API}/crm/contacts?linked_project_id=${projectId}`, opts).catch(() => ({ data: { items: [] } })),
      axios.get(`${API}/crm/opportunities?project_id=${projectId}`, opts).catch(() => ({ data: { items: [] } })),
    ]).then(([c, o]) => {
      const cs = Array.isArray(c.data) ? c.data : (c.data?.items || []); const os = Array.isArray(o.data) ? o.data : (o.data?.items || []);
      setCrm({ contacts: cs.length, opps: os.length, amount: os.reduce((s, x) => s + (x.amount || 0), 0) });
    });
  }, [projectId]);
  const m = ov?.metrics || {};
  const stages = tree?.production?.stages || [];
  return (
    <div className="space-y-5" data-testid="project-dashboard">
      <ProjectTimeline project={project} onNavigateToCalendar={onNavigateToCalendar} />

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Kpi icon={Building2} label="Bâtiments" value={m.buildings ?? tree?.buildings.length ?? '—'} sub={`${tree?.environment.buildings_placed ?? 0} posés sur le terrain`} testid="kpi-buildings" />
        <Kpi icon={Home} label="Lots" value={m.lots ?? '—'} sub={`${m.lots_with_plan ?? 0} avec plan · ${m.lots_with_gen ?? 0} rendus`} testid="kpi-lots" />
        <Kpi icon={Images} label="Rendus validés" value={m.renders_validated ?? '—'} sub={`sur ${m.renders_total ?? 0} produits · ${tree?.production.percent ?? 0} % maquette`} testid="kpi-renders" />
        <Kpi icon={FileText} label="Fichiers" value={m.files ?? '—'} sub="plans, CCTP, références" testid="kpi-files" />
        <Kpi icon={Users} label="Commercial" value={crm.contacts} sub={`${crm.opps} opportunités · ${fmtMoney(crm.amount)}`} testid="kpi-crm" />
      </div>

      {tree ? <ProjectTreeViewer projectId={projectId} tree={tree} onOpen={onOpen} /> : <div className="rounded-2xl border border-[var(--c-border)] bg-[var(--c-surface-0)] h-[320px] flex items-center justify-center text-[12px] text-[var(--c-text-muted)]"><Loader2 className="w-4 h-4 animate-spin mr-2" />Arborescence…</div>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <Card title="Avancement maquette" testid="dash-stages" action={<button type="button" onClick={() => onOpen('production', 'generation')} className="text-[11px] text-[var(--c-gold)] hover:underline inline-flex items-center gap-1" data-testid="dash-open-generation">Génération<ArrowRight className="w-3 h-3" /></button>}>
          {stages.length === 0 && <p className="text-[12px] text-[var(--c-text-muted)]">Aucune caméra produite pour l'instant.</p>}
          <div className="space-y-2.5">
            {stages.map((st, i) => (
              <div key={st.key} data-testid={`dash-stage-${st.key}`}>
                <div className="flex items-center justify-between text-[11.5px]"><span className="text-[var(--c-text-2)]">{i + 1} · {st.label}</span><span className="hud-tabular text-[var(--c-text)]">{st.validated}<span className="text-[var(--c-text-faint)]">/{st.total}</span></span></div>
                <div className="h-1.5 rounded-full bg-[var(--c-surface-2)] overflow-hidden mt-1"><div className={`h-full rounded-full ${st.complete ? 'bg-emerald-500' : 'bg-[var(--c-gold)]'}`} style={{ width: `${st.total ? (100 * st.validated) / st.total : 0}%` }} /></div>
                {(st.to_review || st.stale) ? <p className="text-[10px] text-[var(--c-text-faint)] mt-0.5">{st.to_review ? `${st.to_review} à valider` : ''}{st.to_review && st.stale ? ' · ' : ''}{st.stale ? `${st.stale} à reprendre` : ''}</p> : null}
              </div>
            ))}
          </div>
          {tree && <p className="text-[11px] text-[var(--c-text-muted)] mt-3">{tree.version.name.replace(/\s*·\s*$/, '')} · {tree.production.cameras} caméras · {tree.production.credits_total} crédits IA</p>}
        </Card>

        <Card title="À faire" testid="dash-actions">
          {!tree?.next_actions?.length && <p className="text-[12px] text-emerald-700 inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" />Tout est à jour.</p>}
          <ul className="space-y-2">
            {(tree?.next_actions || []).map((a, i) => (
              <li key={i}>
                <button type="button" onClick={() => onOpen('production', a.level === 'production' ? 'generation' : 'preparation', { buildingId: a.level === 'building' ? a.id : null })} className="w-full text-left text-[12px] text-[var(--c-text-2)] hover:text-[var(--c-text)] inline-flex items-start gap-2" data-testid={`dash-action-${i}`}>
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 text-amber-500 shrink-0" />{a.label}
                </button>
              </li>
            ))}
          </ul>
        </Card>

        <Card title="Dernier rendu validé" testid="dash-hero" action={<button type="button" onClick={() => onOpen('production', 'galerie')} className="text-[11px] text-[var(--c-gold)] hover:underline inline-flex items-center gap-1" data-testid="dash-open-galerie">Galerie<ArrowRight className="w-3 h-3" /></button>}>
          <div className="aspect-video rounded-lg overflow-hidden border border-[var(--c-border)] bg-[var(--c-surface-2)] flex items-center justify-center">
            {ov?.hero ? <img src={`${BASE}${ov.hero.url}`} alt={ov.hero.label || 'Rendu'} className="w-full h-full object-cover" crossOrigin="use-credentials" data-testid="dash-hero-image" /> : <span className="text-[11px] text-[var(--c-text-faint)] inline-flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" />Aucun rendu validé</span>}
          </div>
          {ov?.hero && <p className="text-[11px] text-[var(--c-text-muted)] mt-2 truncate">{ov.hero.label}{ov.hero.validated ? ' · validé' : ''}</p>}
        </Card>
      </div>
    </div>
  );
};

export default ProjectDashboardSection;
